import { patientApi } from '../../patients/api/patientApi';
import { appointmentApi } from '../api/appointmentApi';
import { PatientFormData } from '../../patients/types/patient.types';
import { AppointmentFormData } from '../types/appointment.types';

interface PatientAndAppointmentData {
  patientData: PatientFormData;
  appointmentData: Omit<AppointmentFormData, 'patientId'>;
}

/**
 * Service to handle the combined flow of patient registration and appointment booking
 */
export const appointmentBookingService = {
  /**
   * Register a new patient and book an appointment in a single transaction
   */
  async registerPatientAndBookAppointment(data: PatientAndAppointmentData) {
    try {
      // Check if this is a family member creation (has familyId)
      if (data.patientData.familyId) {
        console.log('Creating family member with familyId:', data.patientData.familyId);
        
        // For family members, we need to handle the duplicate phone number scenario
        // The backend should allow family members to share phone numbers
        try {
          // 1. First, create the family member patient
          const patient = await patientApi.createPatient(data.patientData);
          
          // 2. Then, create the appointment with the new patient ID
          const appointment = await appointmentApi.create({
            ...data.appointmentData,
            patientId: patient.id
          });
          
          return { patient, appointment };
        } catch (createError: any) {
          // If family member creation fails due to phone number conflict, 
          // it means the backend validation needs to be updated to allow
          // family members to share phone numbers
          console.error('Family member creation failed:', createError);
          throw new Error(`Failed to create family member: ${createError.message}`);
        }
      } else {
        // Regular patient creation
        // 1. First, create the patient
        const patient = await patientApi.createPatient(data.patientData);
        
        // 2. Then, create the appointment with the new patient ID
        const appointment = await appointmentApi.create({
          ...data.appointmentData,
          patientId: patient.id
        });
        
        return { patient, appointment };
      }
    } catch (error) {
      console.error('Error in registerPatientAndBookAppointment:', error);
      throw error;
    }
  },

  /**
   * Book an appointment for an existing patient
   */
  async bookAppointmentForExistingPatient(patientId: string, appointmentData: Omit<AppointmentFormData, 'patientId'>) {
    try {
      // Verify patient exists
      await patientApi.getPatientById(patientId);
      
      // Create the appointment
      return await appointmentApi.create({
        ...appointmentData,
        patientId
      });
    } catch (error) {
      console.error('Error in bookAppointmentForExistingPatient:', error);
      throw error;
    }
  }
};

export default appointmentBookingService;
